import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Tooltip } from '@lib/tooltip'
import { FC, useId } from 'react'
import { twJoin } from 'tailwind-merge'

interface Props {
  icon: IconDefinition
  variant: 'holiday' | 'timeOff'
  heading: string
  subheading?: string
  value?: string
}

/**
 * A single non-work element (holiday or absence) shown on the Details tab,
 * beneath the hours table. Colors mirror the day-annotation pills used in the
 * time card tables: teal for holidays, purple for time off/leave.
 */
export const DetailItem: FC<Props> = ({
  icon,
  variant,
  heading,
  subheading,
  value,
}) => {
  const tooltipId = useId()

  return (
    <>
      <div
        data-testid="detail-item"
        data-tooltip-id={tooltipId}
        className={twJoin(
          'flex flex-row items-center justify-between gap-2 border rounded px-3 py-2',
          variant === 'holiday' && 'bg-teal-100 border-teal-300 text-teal-800',
          variant === 'timeOff' &&
            'bg-purple-100 border-purple-300 text-purple-800',
        )}
      >
        <div className="flex flex-row items-center min-w-0 gap-2">
          <FontAwesomeIcon icon={icon} fixedWidth={true} />
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold truncate">{heading}</span>
            {subheading && (
              <span className="text-xs truncate">{subheading}</span>
            )}
          </div>
        </div>
        {value && (
          <span className="text-sm font-semibold whitespace-nowrap">
            {value}
          </span>
        )}
      </div>
      <Tooltip id={tooltipId}>{heading}</Tooltip>
    </>
  )
}
